<template>
	<div
		:class="{
			'app-notification': true,
			[`app-notification--${notification.type}`]: true
		}"
		role="alert"
		aria-live="polite"
	>
		<div class="app-notification__header">
			<div class="app-notification__icon" aria-hidden="true">
				<app-icon
					v-if="['info', 'warning'].includes(notification.type)"
					name="info-circle-outline"
					width="18px"
					color="currentColor"
				/>
				<app-icon
					v-else-if="notification.type === 'danger'"
					name="close"
					width="18px"
					color="currentColor"
				/>
				<app-icon
					v-else
					name="person-success"
					width="18px"
					color="currentColor"
				/>
			</div>

			<div class="app-notification__title-wrapper">
				<h3
					v-if="notification.title"
					class="app-notification__title"
				>
					{{ notification.title }}
				</h3>
				<p class="app-notification__text">{{ notification.text || '' }}</p>
			</div>

			<app-icon
				v-if="notification.closable"
				class="app-notification__close"
				name="close"
				width="14px"
				color="#979797"
				hover-color="#499259"
				cursor="pointer"
				:title="$t('close_label')"
				:aria-label="$t('ui_close_notification')"
				@click="handleClose"
			/>
		</div>

		<div
			v-if="showProgressBar"
			ref="progressBar"
			class="app-notification__progress"
		/>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	onMounted,
	onUnmounted,
	computed
} from 'vue'
import type { Notification } from '@/composables/useNotification'
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

interface AppNotificationItemProps {
	notification: Notification
}

interface AppNotificationItemEmits {
	(e: 'close', id: number): void
}

const props = defineProps<AppNotificationItemProps>()
const emit = defineEmits<AppNotificationItemEmits>()

const progressBar = ref<HTMLElement | null>(null)
const animation = ref<Animation | null>(null)
const startTime = ref<number>(0)
const elapsedTime = ref<number>(0)
const isProgressPaused = ref<boolean>(false)
const isMounted = ref<boolean>(false)

const showProgressBar = computed(() => {
	return props.notification.delay! > 0 && isMounted.value
})

const handleClose = (): void => {
	emit('close', props.notification.id)
}

const startProgressAnimation = (): void => {
	if (!progressBar.value || !showProgressBar.value) return

	const remainingTime = props.notification.delay! - elapsedTime.value

	if (remainingTime <= 0) {
		handleClose()
		return
	}

	animation.value = progressBar.value.animate(
		[
			{ width: '100%' },
			{ width: '0%' }
		],
		{
			duration: remainingTime,
			easing: 'linear',
			fill: 'forwards'
		}
	)

	animation.value.onfinish = () => {
		if (!isProgressPaused.value) {
			handleClose()
		}
	}

	startTime.value = performance.now()
}

onMounted(() => {
	isMounted.value = true

	setTimeout(() => {
		startProgressAnimation()
	}, 10)
})

onUnmounted(() => {
	if (animation.value) {
		animation.value.cancel()
	}
})
</script>

<style scoped lang="scss">
.app-notification {
	position: relative;
	background: white;
	border-radius: 8px;
	padding: 16px;
	box-shadow: 0 1px 4px 0 #00000033;
	overflow: hidden;
	min-width: 320px;
	max-width: 400px;
	transition: transform 0.3s ease, opacity 0.3s ease;
	backdrop-filter: blur(10px);

	&:hover {
		transform: translateY(-2px);
		box-shadow: 1px 1px 4px 0 rgba(0, 0 , 0, 0.25);
	}

	&--success {
		color: #3F884F;
	}

	&--info {
		color: #979797;
	}

	&--warning {
		color: #FB8C00;
	}

	&--danger {
		color: #F44336;
	}

	&__header {
		display: flex;
		align-items: flex-start;
		gap: 12px;
	}

	&__icon {
		flex-shrink: 0;
	}

	&__title-wrapper {
		flex: 1;
		min-width: 0;
	}

	&__title {
		margin: 0 0 4px 0;
		font-size: 16px;
		font-weight: 500;
		line-height: 20px;
		color: #333;
	}

	&__text {
		margin: 0;
		font-size: 14px;
		line-height: 18px;
		color: #333;
		word-break: break-word;
	}

	&__progress {
		position: absolute;
		bottom: 0;
		left: 0;
		height: 3px;
		background: currentColor;
		opacity: 0.5;
		border-radius: 0 0 0 4px;
	}
}

@keyframes progress {
	from {
		width: 100%;
	}
	to {
		width: 0;
	}
}

@media (max-width: 480px) {
	.notification {
		min-width: unset;
		max-width: calc(100vw - 40px);
		margin: 0 20px;
	}

	.notifications-container {
		right: 0;
		left: 0;
		max-width: 100%;
	}
}
</style>
